import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Eraser, Type, PenTool } from 'lucide-react';

/**
 * SignaturePad
 *
 * Lets the user either draw a signature on a canvas or type their name.
 * Emits the resulting data URL (data:image/png;base64,...) or typed string via `onChange`.
 *
 * Props:
 *  - value: current stored signature (data URL or typed name)
 *  - onChange(next): called whenever the signature changes
 *  - typedName / onTypedNameChange: also expose the typed-name shadow value
 */
export default function SignaturePad({ value, onChange, typedName = '', onTypedNameChange, testId = 'signature-pad' }) {
  const sigRef = useRef(null);
  const wrapRef = useRef(null);
  const [mode, setMode] = useState(value && value.startsWith('data:image') ? 'draw' : 'type');
  const [canvasWidth, setCanvasWidth] = useState(500);

  // Keep the canvas width responsive to its container so mobile users
  // aren't clipped when the enrollment page renders under 500px.
  useEffect(() => {
    if (!wrapRef.current) return;
    const measure = () => {
      const w = wrapRef.current?.clientWidth || 500;
      setCanvasWidth(Math.max(280, w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // When the draw canvas mounts (or mode flips) rehydrate any saved dataURL.
  useEffect(() => {
    if (mode !== 'draw' || !sigRef.current) return;
    if (value && value.startsWith('data:image')) {
      try { sigRef.current.fromDataURL(value); } catch { /* noop */ }
    }
  }, [mode, value]);

  const handleEnd = () => {
    if (!sigRef.current) return;
    const data = sigRef.current.isEmpty() ? '' : sigRef.current.toDataURL('image/png');
    onChange(data);
  };

  const handleClear = () => {
    sigRef.current?.clear();
    onChange('');
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-lg border bg-white p-0.5">
          <button
            type="button"
            onClick={() => setMode('draw')}
            className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 transition ${
              mode === 'draw' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid={`${testId}-mode-draw`}
          >
            <PenTool className="w-3 h-3" /> Draw
          </button>
          <button
            type="button"
            onClick={() => setMode('type')}
            className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 transition ${
              mode === 'type' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid={`${testId}-mode-type`}
          >
            <Type className="w-3 h-3" /> Type
          </button>
        </div>
        {mode === 'draw' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-7 text-xs text-red-600 hover:text-red-700"
            data-testid={`${testId}-clear`}
          >
            <Eraser className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>

      {mode === 'draw' ? (
        <div
          ref={wrapRef}
          className="rounded-lg border-2 border-dashed border-primary/40 bg-white overflow-hidden"
          data-testid={`${testId}-canvas-wrapper`}
        >
          <SignatureCanvas
            ref={sigRef}
            penColor="#111827"
            backgroundColor="#ffffff"
            canvasProps={{
              width: canvasWidth,
              height: 160,
              className: 'w-full h-40 touch-none cursor-crosshair',
              'data-testid': `${testId}-canvas`,
            }}
            onEnd={handleEnd}
          />
          <p className="text-[10px] text-center py-1 text-gray-400 bg-gray-50 border-t border-dashed border-primary/20">
            Sign above with your finger, stylus, or mouse
          </p>
        </div>
      ) : (
        <div>
          <Label htmlFor={`${testId}-typed`} className="sr-only">Typed signature</Label>
          <Input
            id={`${testId}-typed`}
            placeholder="Type your full name"
            value={typedName}
            onChange={(e) => {
              onTypedNameChange?.(e.target.value);
              onChange(e.target.value);
            }}
            data-testid={`${testId}-typed`}
            className="font-signature italic text-lg"
            style={{ fontFamily: '"Dancing Script","Segoe Script",cursive' }}
          />
          <p className="text-[10px] text-gray-400 mt-1">
            Typing your full legal name counts as a signature under Wisconsin electronic signature law.
          </p>
        </div>
      )}
    </div>
  );
}
